import React, { useCallback, useContext, useMemo, useRef } from 'react'
import LayoutContainer from 'components/Common/LayoutContainer'
import { rem } from 'polished'
import moment from 'moment'
import styled from 'styled-components'
import TextButton from 'LuxKit/Button/TextButton'
import useToggle from 'hooks/useToggle'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import { OfferPageActions, OfferPageMultiRoomAutoselectionReason, OfferPagePriceMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import Caption from 'LuxKit/Typography/Caption'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import MultipleRoomsBookingContentFooter from './MultipleRoomsBookingContentFooter'
import {
  getActivePackagesByRoom,
  getAvailablePackagesByRoom,
  getCalendarsToBeFetchedByRoom, getIsLoadingByRoom,
  LoadingStatusByRoom,
  PackageOptionViewByRoom,
  PackagesOptionsViewsByRoom
} from 'selectors/offerPage/multipleRoomBookingSelectors'
import { useAppDispatch } from 'hooks/reduxHooks'
import BnblModal from '../BnblModal/BnblModal'
import { initialiseBnblOfferModal } from 'actions/BnblModalActions'
import { EmptyArray, groupBy, nonNullable } from 'lib/array/arrayUtils'
import { getBnblPackageViews } from 'selectors/offerPage/offerPageSelectors'
import { getBnblPackageViewByNumberOfRooms } from '../BnblModal/utils'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import PriceStackLuxPlusCompactPrice from 'LuxKit/PricePoints/LuxPlus/PriceStackLuxPlusCompactPrice'
import Pane from 'components/Common/Pane'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal, { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal'
import MultiRoomOfferBookButton from './MultiRoomOfferBookButton'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import { NO_24_7_SUPPORT_REGIONS } from 'constants/config/region'
import GeoContext from 'contexts/geoContext'
import ContactContext from 'contexts/contactContext'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import RequestAgentCallbackModal from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import clsx from 'clsx'
import { generateLoyaltyPointsCalculatorHotelOptions, generateLoyaltyPointsCalculatorBundledFlightOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import Group from 'components/utils/Group'
import MultiRoomOfferAddToCartButton from './MultiRoomOfferAddToCartButton'
import MultiRoomOfferSelectRoomsButton from './MultiRoomOfferSelectRoomsButton'
import Divider from 'LuxKit/Divider'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import MultiIndividualRoomRoom from './MultiIndividualRoomRoom'
import PreSelectionBanner from './PreSelectionBanner'
import InvalidSelectionBanner from './InvalidSelectionBanner'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import { pluralizeToString } from 'lib/string/pluralize'
import { getPriceDisplayFunction } from 'lib/offer/offerPageUtil'

const PackageOptionsContainer = styled(LayoutContainer)`
  position: relative;
  
  ${mediaQueryDown.mobile} {
    padding: 0;
  }
`

const MobilePaddingGroup = styled(Group)`
  background-color: ${props => props.theme.palette.neutral.default.eight};
  padding: ${rem(20)};

  ${mediaQueryUp.desktop} {
    padding: 0;
  }
`

const GreyGroup = styled(Group)`
  background-color: ${props => props.theme.palette.neutral.default.six};

  ${mediaQueryUp.tablet} {
    background-color: ${props => props.theme.palette.neutral.default.eight};
  }
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};

  ${mediaQueryUp.desktop} {
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
  }

  &.with-banner {
    flex-direction: column;
    margin-top: ${rem(8)};
    gap: ${rem(8)};

    ${mediaQueryUp.desktop} {
      flex-direction: row;
      margin-top: 0;
      align-self: flex-end;
      padding: 0;
    }
  }
`

interface Props {
  rooms?: Array<OfferPageRoom>
  offer: App.HotelOffer
  activePackagesByRoom: PackageOptionViewByRoom
  isLoadingByRoom: LoadingStatusByRoom
  calendarsToBeFetchedByRoom: LoadingStatusByRoom
  availablePackagesByRoom: PackagesOptionsViewsByRoom
  account?: App.AuthAccount
  loggedIn?: boolean
  baseBnblPackageViews: Array<App.OfferPackageOptionView>
  canRedeemLuxPlusBenefits: boolean
  checkIn?: string
  autoselectionReason?: OfferPageMultiRoomAutoselectionReason
  checkInDate: string | undefined
  checkOutDate: string | undefined
  hasFlights: boolean
  charterBundleRoute?: App.CharterBundleRoute
  priceMode: OfferPagePriceMode
}

function MultipleRoomsBookingContent({
  rooms = EmptyArray,
  offer,
  activePackagesByRoom,
  isLoadingByRoom,
  calendarsToBeFetchedByRoom,
  availablePackagesByRoom,
  loggedIn: isLoggedIn,
  baseBnblPackageViews,
  canRedeemLuxPlusBenefits,
  checkIn,
  autoselectionReason,
  checkInDate,
  checkOutDate,
  hasFlights,
  charterBundleRoute,
  priceMode,
}: Props) {
  const showModal = useContext(ModalContext)
  const offerPageDispatch = useOfferPageDispatch()
  const dispatch = useAppDispatch()

  const { currentRegionCode } = useContext(GeoContext)
  const currentContact = useContext(ContactContext)

  const nights = checkInDate && checkOutDate ? moment(checkOutDate).diff(checkInDate, 'days') : 1
  const { totalFlightPrice, loading: flightPriceLoading } = useOfferPageFlightPrice()

  const missingCharterFlightPrice = !!charterBundleRoute && !totalFlightPrice && !flightPriceLoading

  const [buyNowBookLater, _, openBNBLModal, closeBNBLModal] = useToggle()

  const {
    totalRoomsPrice,
    totalRoomsBaseMemberPrice,
    totalRoomsMemberPrice,
    totalRoomsFlightPrice,
  } = useMemo(() => (
    Object.keys(activePackagesByRoom).reduce((acc, roomId) => {
      if (!activePackagesByRoom[roomId]) return acc

      const {
        hotelPrice,
        hotelMemberPrice,
        baseMemberPrice,
        flightPrice,
        propertyFees = 0,
      } = activePackagesByRoom[roomId]

      const totalHotelPrice = hotelPrice + propertyFees
      const totalHotelMemberPrice = hotelMemberPrice + propertyFees
      const totalRoomPrice = hasFlights ? totalHotelPrice + flightPrice : totalHotelPrice
      const totalRoomMemberPrice = hasFlights ? totalHotelMemberPrice + flightPrice : totalHotelMemberPrice
      const totalFlightPrice = hasFlights ? flightPrice : 0

      return {
        totalRoomsPrice: acc.totalRoomsPrice + totalRoomPrice,
        totalRoomsMemberPrice: acc.totalRoomsMemberPrice + (totalRoomMemberPrice || totalRoomPrice),
        totalRoomsBaseMemberPrice: acc.totalRoomsBaseMemberPrice + baseMemberPrice,
        totalRoomsFlightPrice: acc.totalRoomsFlightPrice + totalFlightPrice,
      }
    }, { totalRoomsPrice: 0, totalRoomsMemberPrice: 0, totalRoomsBaseMemberPrice: 0, totalRoomsFlightPrice: 0 })

  ), [activePackagesByRoom, hasFlights])

  const resolvedTotalFlightPrice = hasFlights ? (totalFlightPrice ?? totalRoomsFlightPrice) : 0

  const isBookNowBtnDisabled = useMemo(() => (
    Object.values(isLoadingByRoom).some(Boolean) ||
    Object.values(calendarsToBeFetchedByRoom).some(Boolean) ||
    Object.values(activePackagesByRoom).some(pkg => !pkg)
  ), [isLoadingByRoom, calendarsToBeFetchedByRoom, activePackagesByRoom])

  const onOpenBnblModal = useCallback(async(e: React.MouseEvent<HTMLAnchorElement>) => {
    const openAndInitaliseBnblModal = () => {
      e.preventDefault()
      openBNBLModal()

      offerPageDispatch({
        type: OfferPageActions.toggleFlightsPrice,
        isFlightsSelected: false,
      })

      const selectedPackages = nonNullable(Object.values(activePackagesByRoom))
      const groupedPackagesRecords = groupBy(selectedPackages, pkg => pkg.package.uniqueKey)
      const packages = [...groupedPackagesRecords.entries()].map(groupedPackage => {
        const basePackage = baseBnblPackageViews.find(packageView => packageView.package.uniqueKey === groupedPackage[0])!
        const numberOfRooms = groupedPackage[1].length

        return {
          packageView: getBnblPackageViewByNumberOfRooms(basePackage, numberOfRooms),
          numberOfRooms,
        }
      })

      dispatch(initialiseBnblOfferModal(packages, offer.id, false))
    }

    if (!isLoggedIn) {
      e.preventDefault()

      const result = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
      if (result.loggedIn) {
        openAndInitaliseBnblModal()
      }
    }

    if (isLoggedIn) {
      openAndInitaliseBnblModal()
    }
  }, [isLoggedIn, openBNBLModal, offerPageDispatch, activePackagesByRoom, dispatch, offer.id, baseBnblPackageViews, showModal])

  const shouldPreventMultipleRoomsBooking = (rooms.length > 2 && canRedeemLuxPlusBenefits && totalRoomsBaseMemberPrice > 0) || missingCharterFlightPrice

  const canShowPrice = !missingCharterFlightPrice

  const callbacksFeatureEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.hotelsAndVillasRequestAgentCallbackEnabled)
  const callbackButtonProps = callbacksFeatureEnabled ? {
    children: 'Request a callback',
    onClick: () => showModal(<RequestAgentCallbackModal type="HOTELS_AND_VILLAS" />),
  } : undefined

  const packageStartRef = useRef<HTMLDivElement>(null)

  const loyaltyHotelOptions = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    return Object.values(activePackagesByRoom)?.map(packageView => (
      generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView, currentRegionCode, nights: packageView?.package?.duration })
    ))
  }, [offer, activePackagesByRoom, currentRegionCode])

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    ...loyaltyHotelOptions,
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
      flightPrice: resolvedTotalFlightPrice,
      skip: !hasFlights,
    }),
  ], [offer, hasFlights, resolvedTotalFlightPrice, loyaltyHotelOptions])

  const roomsAreSelected = rooms.some(room => room.roomId)
  let selectionBanner: React.ReactNode = null
  if (roomsAreSelected) {
    if (autoselectionReason === 'initial-selection') {
      selectionBanner = <PreSelectionBanner saleUnit={offer.saleUnit} />
    } else if (autoselectionReason === 'invalid-selections') {
      selectionBanner = <InvalidSelectionBanner saleUnit={offer.saleUnit} />
    }
  }

  const showBNBL = offer.hasBuyNowBookLater && !shouldPreventMultipleRoomsBooking && !charterBundleRoute

  const convertToDisplay = getPriceDisplayFunction(priceMode, rooms)

  return (
    <Pane ref={packageStartRef}>
      <PackageOptionsContainer>
        <CSSBreakpoint max="mobile">
          <Divider kind="primary" />
        </CSSBreakpoint>
        <GreyGroup direction="vertical" gap={8} tabletGap={24}>
          {rooms.map((room, i) => {
            const packageOptionView = activePackagesByRoom[room.id]
            const roomTile = <MultiIndividualRoomRoom
              key={room.id}
              room={room}
              packageNumber={i + 1}
              packageView={packageOptionView}
              isFetching={!!(isLoadingByRoom[room.id] || calendarsToBeFetchedByRoom[room.id])}
              availablePackages={availablePackagesByRoom[room.id]}
            />

            if (i === 0 && selectionBanner) {
              return <div key={room.id}>
                {selectionBanner}
                {roomTile}
              </div>
            } else {
              return roomTile
            }
          })}

          <MobilePaddingGroup direction="vertical" gap={20}>
            <Group direction="horizontal" tabletHorizontalAlign="end">
              <LoyaltyPointsEarnWithBreakdownModal
                calculationRequests={pointsEarnCalculationRequests}
                calculationType={(!checkIn || hasFlights) ? 'estimate' : 'exact'}
              />
            </Group>

            <MultipleRoomsBookingContentFooter>
              <Wrapper className={clsx({ 'with-banner': shouldPreventMultipleRoomsBooking })}>
                {showBNBL && (
                  <CSSBreakpoint min="desktop">
                    <TextButton kind="tertiary" size="large" onClick={onOpenBnblModal} horizontalOutdent="start">
                      Buy now choose dates later
                    </TextButton>
                    <Caption variant="medium" as="span" wrap="no-wrap">
                      Travel until {moment(offer.travelToDate).format(DMY_CASUAL_FORMAT)}
                    </Caption>
                  </CSSBreakpoint>
                )}

                {!showBNBL && <div />}

                <Group direction="horizontal" horizontalAlign="space-between" tabletHorizontalAlign="end" verticalAlign="center" gap={24}>
                  {canShowPrice && <LuxPlusPriceGuard
                    showMemberPrice={totalRoomsBaseMemberPrice > 0}
                    fallbackPrice={
                      <div>
                        <PriceRowPriceCaption>
                          {pluralizeToString('night', nights)}, {rooms.length} rooms {hasFlights && !!resolvedTotalFlightPrice && '+ flights'} {hasFlights ? 'from' : ''}
                        </PriceRowPriceCaption>
                        <PriceRowPrice
                          size="S"
                          price={convertToDisplay(totalRoomsPrice + resolvedTotalFlightPrice)}
                          saleUnit={priceMode === 'perPerson' ? 'person' : 'total'}
                        />
                        {!resolvedTotalFlightPrice && hasFlights && <Caption variant="large">+ flights</Caption>}
                      </div>
                    }
                  >
                    <div>
                      <PriceRowPriceCaption>
                        {pluralizeToString('night', nights)}, {rooms.length} rooms {hasFlights && !!resolvedTotalFlightPrice && '+ flights'} {hasFlights ? 'from' : ''}
                      </PriceRowPriceCaption>
                      <PriceStackLuxPlusCompactPrice
                        size="M"
                        memberPrice={convertToDisplay(totalRoomsMemberPrice + resolvedTotalFlightPrice)}
                        saleUnit={priceMode === 'perPerson' ? 'person' : 'total'}
                        nonMemberPrice={convertToDisplay(totalRoomsPrice + resolvedTotalFlightPrice)}
                        horizontalAlign="start"
                      />
                      {!resolvedTotalFlightPrice && hasFlights && <Caption variant="large">+ flights</Caption>}
                    </div>
                  </LuxPlusPriceGuard>}
                  {!shouldPreventMultipleRoomsBooking && <Group direction="vertical-reverse" tabletDirection="horizontal" gap={8}>
                    {!hasFlights && <MultiRoomOfferAddToCartButton disabled={isBookNowBtnDisabled} fit="flex" size="large" />}
                    {hasFlights && <MultiRoomOfferSelectRoomsButton disabled={isBookNowBtnDisabled} fit="flex" size="large" flightPrice={resolvedTotalFlightPrice} />}
                    {!hasFlights && <MultiRoomOfferBookButton disabled={isBookNowBtnDisabled} size="large" />}
                  </Group>}
                </Group>

                {showBNBL && (
                  <CSSBreakpoint max="tablet">
                    <Group direction="horizontal" gap={8} verticalAlign="center">
                      <TextButton kind="tertiary" size="large" onClick={onOpenBnblModal} horizontalOutdent="start">
                        Buy now choose dates later
                      </TextButton>
                      <Caption variant="medium" as="span" wrap="no-wrap">
                        Travel until {moment(offer.travelToDate).format(DMY_CASUAL_FORMAT)}
                      </Caption>
                    </Group>
                  </CSSBreakpoint>
                )}
                {shouldPreventMultipleRoomsBooking && <MessageBanner
                  kind="informational-primary"
                  heading="Ready to book? Call us to complete your booking"
                  primaryActionButtonProps={{
                    startIcon: <LinePhoneIcon />,
                    children: `Call ${currentContact.defaultContact.humanReadable}`,
                    href: `tel:${currentContact.defaultContact.number}`,
                  }}
                  secondaryActionButtonProps={callbackButtonProps}
                >
                  Our team are available {NO_24_7_SUPPORT_REGIONS.includes(currentRegionCode) ? '7 days a week from 9am to 7pm' : '24 hours, 7 days'}
                </MessageBanner>}
              </Wrapper>
            </MultipleRoomsBookingContentFooter>
          </MobilePaddingGroup>

          {buyNowBookLater && <BnblModal
            closeBnblModal={closeBNBLModal}
            isOpen={buyNowBookLater}
            offer={offer}
          />}
        </GreyGroup>
      </PackageOptionsContainer>
    </Pane>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  rooms: state.rooms,
  offer: state.offer,
  account: appState.auth.account,
  activePackagesByRoom: getActivePackagesByRoom(state, appState),
  isLoadingByRoom: getIsLoadingByRoom(state, appState),
  calendarsToBeFetchedByRoom: getCalendarsToBeFetchedByRoom(state, appState),
  availablePackagesByRoom: getAvailablePackagesByRoom(state, appState),
  loggedIn: selectLoggedIn(appState),
  baseBnblPackageViews: getBnblPackageViews(state),
  canRedeemLuxPlusBenefits: getCanRedeemLuxPlusBenefits(appState),
  checkIn: state.checkInDate,
  autoselectionReason: state.multiRoomAutoselectionReason,
  checkInDate: state.checkInDate,
  checkOutDate: state.checkOutDate,
  hasFlights: !!state.offer?.offerFlightsEnabled && state.flights,
  charterBundleRoute: state.charterBundleRoute,
  priceMode: state.priceMode,
}), MultipleRoomsBookingContent)
